import { useCallback, useEffect, useState, type ReactNode } from 'react'
import {
  Button,
  Card,
  Chip,
  ChipGroup,
  Drawer,
  ErrorState,
  LoadingState,
  Meta,
  Meter,
  Reading,
  ReadingSection,
  Stat,
  TileGrid,
  Timeline,
} from '../../ds'
import type { KnowledgePerson } from '../../services/api'
import CustomerKnowledge from './CustomerKnowledge'
import CustomerErasure from './CustomerErasure'
import ProductKnowledge, { humanise, type CardHeader } from './ProductKnowledge'
import { fmtCurrency, lifecycleStyle, recordName } from './knowledgeIndex'
import type { ShownCustomer, ShownDrawer, ShownProduct } from './useKnowledgeDrawers'

/**
 * The Knowledge drawer — ONE drawer for a person or a product (Knowledge v6 PR 2, MAR-326).
 *
 * Replaces `KnowledgeDrawers.tsx` (the two old `Memory.tsx` drawers, moved in MAR-325). The
 * layout is `Knowledge Drawer v7 (merged).html` in the Claude Design project: v6's structure
 * — eyebrow header (now `Drawer size="sm"`, MAR-372), fact rows with a source chip and provenance, the card's rows,
 * "What the card is missing", erasure as its own section — MERGED with the old drawer's
 * tiles, Signals, memory, Still open and Recent. Signals keeps the engagement meter labelled
 * with its stored LEVEL — never a %, because the old 30/60/88 mapping printed a precision the
 * backend never held. MAR-36 removed the stale nightly churn meter and fixed next-action line.
 * There is still no header confidence and no synthesized per-person number.
 *
 * ⚠️ Mounted UNCONDITIONALLY — ONE `Drawer` element whatever is shown — with `open` the only
 * thing that changes (MAR-381: a per-subject Drawer remounted, so it never slid). It is the design
 * system's `Drawer size="sm"` (MAR-372), which owns the `--dockw` inset, the scrim, the focus
 * trap and the ONE Escape stack — Escape closes it only while it is open, so a drawer masked
 * by a workspace switch (not open) is never erased by one (MAR-205).
 *
 * ⚠️ Everything outside `CustomerKnowledge` / `ProductKnowledge` is the RECORD IN HAND, not
 * the knowledge read: a knowledge-api 500, or the latency before it lands, must not blank
 * numbers that route never sourced (MAR-204 PR 1's rule).
 *
 * ⚠️ And the customer's tiles come from a RECORD only: the full one (`customer.full`), or
 * the index row it was opened from (`customer.row`), whose list facts are real and stay
 * while the full read loads or after it fails. A drawer opened from a least-sure row or a
 * deep link holds a name (or only an id) and nothing else; until the record lands it says
 * it is reading, and if the read fails or returns nothing it says so with a retry — never
 * "0 orders" or "£0" about a person the page never read. The intelligence sections read
 * `full` alone.
 */

/** A value read back from a child, stamped with the record it describes and matched at the read. */
interface Stamped<T> {
  key: string
  value: T
}
const stampKey = (workspaceId: string, id: string) => `${workspaceId}::${id}`

export default function KnowledgeDrawer({
  shown,
  workspaceId,
  onClose,
  onExited,
  onRetry,
  onAsk,
  onRelabel,
}: {
  shown: ShownDrawer | null
  workspaceId?: string
  onClose: () => void
  /** The slide-out has finished (`useDrawerPresence`). */
  onExited?: () => void
  onRetry: () => void
  onAsk: () => void
  /** Re-label the Ask pin once a record names itself (`useKnowledgeDrawers.relabel`). */
  onRelabel?: (subject: 'customer' | 'product', id: string, label: string) => void
}) {
  // ONE Drawer element for both subjects and for nothing shown (MAR-381). Each subject only
  // composes what the drawer says; the Drawer itself never remounts, so opening slides in,
  // closing keeps the last record on screen through the slide-out, and moving from a person to
  // a product (or between people) while open replays nothing. Both views are always computed
  // (they hold state), and only the shown subject's is non-null.
  const customer = useCustomerView(shown?.subject === 'customer' ? shown : null, workspaceId, onRetry, onAsk)
  const product = useProductView(shown?.subject === 'product' ? shown : null, workspaceId, onAsk, onRelabel)
  const view = customer ?? product
  return (
    <Drawer
      id={DRAWER_ID}
      open={!!view}
      // Held through a close — never through a workspace switch (MAR-205).
      scope={workspaceId}
      modal={false}
      onClose={onClose}
      onExited={onExited}
      size="sm"
      eyebrow={view?.eyebrow}
      title={view?.title ?? ''}
      subtitle={view?.subtitle}
      footer={view?.footer}
    >
      {view?.body}
    </Drawer>
  )
}

/** What one subject puts in the drawer. */
interface DrawerView {
  eyebrow: string
  title: string
  subtitle?: ReactNode
  footer: ReactNode
  body: ReactNode
}

/** The drawer's one id — there is one Knowledge drawer, whichever subject it shows. */
const DRAWER_ID = 'knowledge-drawer'


/**
 * The engagement meter's FILL for a stored level. ⚠️ A fill only, never printed: the label
 * is the level word itself. Three thirds because the enum has three values, not because the
 * backend measured anything finer.
 */
const ENGAGE_FILL: Record<string, number> = { low: 33, medium: 66, high: 100 }

/**
 * A stored KEY as words (MAR-412): `chat_engaged` → "Chat engaged", `low` → "Low". Only a
 * lowercase key-shaped value (one token, no spaces) is touched — a trait that is already a sentence
 * keeps its own casing, which `humanise` would flatten.
 */
export function keyText(v: string): string {
  return /^[a-z0-9]+(?:[_.-][a-z0-9]+)*$/.test(v) ? humanise(v.replace(/\./g, ' ')) : v
}

function useCustomerView(
  c: ShownCustomer | null,
  workspaceId: string | undefined,
  onRetry: () => void,
  onAsk: () => void
): DrawerView | null {
  /**
   * The belief read's `person`, lifted from `CustomerKnowledge`. Stamped with the record it
   * describes and matched at the READ, so one person's masked address is never offered to
   * another's erasure for the frame before an effect could clear it.
   */
  const [personFor, setPersonFor] = useState<Stamped<KnowledgePerson | null> | null>(null)
  const onPerson = useCallback(
    (ws: string, id: string, person: KnowledgePerson | null) => setPersonFor({ key: stampKey(ws, id), value: person }),
    []
  )
  if (!c) return null
  const person =
    workspaceId && personFor && personFor.key === stampKey(workspaceId, c.customerId) ? personFor.value : null

  const full = c.detail === 'ready' ? c.full : null
  /** The record the tiles speak from: the full one, else the index row in hand. */
  const record = full ?? c.row ?? null
  const ci = full?.customerIntelligence
  const oi = full?.orderIntelligence

  // The header name: the opener's (a least-sure label's verified name, or a search row's
  // `recordName` — first and last name, `null` when it has neither), else the record's, else
  // the belief read's `person`, else the id — a cold deep link holds only that. ⚠️ Never an
  // address: `custName` falls back to the email, so it must not reach this line.
  const personName = person ? `${person.firstName || ''} ${person.lastName || ''}`.trim() || null : null
  const name = c.name ?? recordName(record) ?? personName ?? (c.customerId || 'Customer')
  // ⚠️ MASKED only. The belief read's `emailMasked`, or a label opener's (already masked) —
  // a row opener's `email` is the real address, and an id line is no place to print it.
  const maskedEmail = person?.emailMasked ?? (c.row || c.full ? null : c.email ?? null)
  // The stage only from the FULL record: the index row omits the intelligence blob, so its
  // stage was never read.
  const stage = full ? lifecycleStyle(full)?.label : null
  const idLine = [c.customerId, maskedEmail, stage].filter(Boolean).join(' · ')

  const level = ci?.communicationPreference?.engagementLevel
  const engageFill = level ? ENGAGE_FILL[level] : undefined

  const traits = [
    ...(ci?.segments || []),
    ...(ci?.behavioralInsights?.decisionDrivers || []),
    ...(ci?.behavioralInsights?.predictedNeeds || []),
  ].slice(0, 8)
  const memory = ci?.synthesizedDescription || ci?.behavioralInsights?.shoppingPsychology
  const openItems = ci?.openItems || []
  // ⚠️ Everything derived here stays sourced from the RECORD IN HAND, not from
  // `knowledge-api`'s `person`. Three of these fields — `decisionDrivers`, `predictedNeeds`
  // and `shoppingPsychology` — are not in that projection at all.
  const recent: [string, string][] = []
  ;(oi?.reorderCandidates || []).slice(0, 2).forEach((r) => recent.push([`${r.dueInDays}d`, `Likely to reorder ${r.title}`]))

  return {
    eyebrow: 'Customer',
    title: name,
    // No header confidence: a per-person % appears only in Signals (decision 3a).
    subtitle: idLine ? <Meta>{idLine}</Meta> : undefined,
    footer: (
      <Button variant="gold" onClick={onAsk}>
        Ask about {full?.firstName || name.split(' ')[0]}
      </Button>
    ),
    body: (
      <Reading key="customer" data-subject="customer">
        {record && (
          <TileGrid size="sm" data-testid="kn-stats">
            <Card padding="sm">
              <Stat tone="gold" value={record.ordersCount || 0} label="orders" />
            </Card>
            <Card padding="sm">
              <Stat value={fmtCurrency(record.totalSpent)} label="lifetime" />
            </Card>
          </TileGrid>
        )}
        {!full &&
          (c.detail === 'failed' ? (
            <ErrorState
              title={
                record
                  ? 'She couldn’t read the rest of this customer’s record just now, so what she remembers of them is not shown rather than shown as nothing.'
                  : 'She couldn’t read this customer’s record just now, so their orders and spend are not shown rather than shown as nothing.'
              }
              onRetry={onRetry}
            />
          ) : (
            <LoadingState caption="Reading her memory…" />
          ))}

        {/* Signals — the full record only. Engagement is its stored level WORD. */}
        {engageFill != null && (
          <ReadingSection label="Signals" testId="kn-signals">
            <Meter
              layout="inline"
              size="sm"
              tone="sage"
              label="Engagement"
              value={engageFill}
              valueText={level ? keyText(level) : undefined}
            />
          </ReadingSection>
        )}

        {/* What she believes, and why — the `knowledge-api` read. OUTSIDE the record gate on
            purpose: a different read of a different table, with its own three states. */}
        <CustomerKnowledge workspaceId={workspaceId} customerId={c.customerId} onPerson={onPerson} />

        {full && (
          <>
            {(memory || traits.length > 0) && (
              <ReadingSection label="What Marva remembers" testId="kn-remembers">
                {memory && (
                  <Card tone="recessed" padding="sm" data-testid="kn-memory">
                    {memory}
                  </Card>
                )}
                {traits.length > 0 && (
                  <ChipGroup>
                    {traits.map((t, i) => (
                      <Chip key={i}>{keyText(t)}</Chip>
                    ))}
                  </ChipGroup>
                )}
              </ReadingSection>
            )}

            {openItems.length > 0 && (
              <ReadingSection label="Still open" testId="kn-open">
                <Timeline label="Still open" items={openItems.map((o) => ({ when: 'Open', title: o }))} />
              </ReadingSection>
            )}

            {recent.length > 0 && (
              <ReadingSection label="Recent" testId="kn-recent">
                <Timeline label="Recent" items={recent.map(([t, d]) => ({ when: t, title: d }))} />
              </ReadingSection>
            )}
          </>
        )}

        {/* Erasure — its own section, re-hosted from inside `CustomerKnowledge` (MAR-326).
            ⚠️ Mounted whatever the belief read did: a failed or slow belief read costs the
            merchant their beliefs and NOT the floor-claim control — `fetchHypotheses` is the
            half that 500s, and the customer can be perfectly erasable while her theories are
            unreadable. `emailMasked` is then null and the confirmation names the record's own
            id instead, which is exact. The mask comes from the belief read's `person` because
            it exists nowhere else: §9.1 masks rather than redacts it *because the erasure
            confirmation has to name a person*, and the drawer's own record carries the
            unmasked address, not the mask. */}
        {/* An id-less row is not a record the door can be asked about — a request built
            from a blank id would address the workspace's whole customer path. */}
        {workspaceId && c.customerId && (
          <ReadingSection label="Erasure" testId="kn-erasure">
            <p>Everything she holds on this person, gone in one action.</p>
            <CustomerErasure
              workspaceId={workspaceId}
              customerId={c.customerId}
              emailMasked={person?.emailMasked ?? null}
            />
          </ReadingSection>
        )}
      </Reading>
    ),
  }
}

/**
 * One product, as she holds it. The card, its beliefs and its gaps are read by
 * `ProductKnowledge` from `GET …/knowledge/products/{productId}`; the header and the two
 * tiles are the catalogue row in hand — and only a REAL row: a least-sure or deep-link open
 * has none, so the tiles are omitted rather than printed as "—".
 */
function useProductView(
  p: ShownProduct | null,
  workspaceId: string | undefined,
  onAsk: () => void,
  onRelabel?: (subject: 'customer' | 'product', id: string, label: string) => void
): DrawerView | null {
  /** The card's own title and type, lifted from `ProductKnowledge` and matched at the read. */
  const [cardFor, setCardFor] = useState<Stamped<CardHeader | null> | null>(null)
  const onCard = useCallback(
    (ws: string, id: string, header: CardHeader | null) => setCardFor({ key: stampKey(ws, id), value: header }),
    []
  )
  const card =
    p && workspaceId && cardFor && cardFor.key === stampKey(workspaceId, p.productId) ? cardFor.value : null
  const cardTitle = card?.title ?? null
  const pTitle = p?.title ?? null
  const pId = p?.productId ?? null

  // An opener with no title (a deep link, or a least-sure row whose title was withheld)
  // pinned "Product <id>"; once the card names the product, the dock names it too. The hook
  // re-pins only while this is still the record on screen, in the live workspace.
  useEffect(() => {
    if (pId != null && !pTitle && cardTitle) onRelabel?.('product', pId, cardTitle)
  }, [pTitle, pId, cardTitle, onRelabel])

  if (!p) return null
  const row = p.row
  const title = p.title || card?.title || p.productId || 'Product'
  const idLine = [p.productId, row?.vendor, row?.productType || card?.productType, row?.status]
    .filter(Boolean)
    .join(' · ')

  return {
    eyebrow: 'Product',
    title,
    subtitle: idLine ? <Meta>{idLine}</Meta> : undefined,
    footer: (
      <Button variant="gold" onClick={onAsk}>
        Ask about {title}
      </Button>
    ),
    body: (
      <Reading key="product" data-subject="product">
        {row && (
          <TileGrid size="sm" data-testid="kn-stats">
            <Card padding="sm">
              <Stat tone="gold" value={row.variantCount ?? '—'} label="variants" />
            </Card>
            <Card padding="sm">
              <Stat value={row.totalInventory ?? '—'} label="in stock" />
            </Card>
          </TileGrid>
        )}

        <ProductKnowledge workspaceId={workspaceId} productId={p.productId} onCard={onCard} />
      </Reading>
    ),
  }
}
